import { useCallback, useEffect, useState } from 'react';
import type { PersonSummary } from '../lib/credential';
import { MAX_PIN_ATTEMPTS, PadLockedError, PinError, padState } from '../lib/credential';
import { unlockWithPinOnly } from '../lib/session';
import { ApiError } from '../lib/api';

/**
 * The PIN pad. Wording and layout from prototype.html (#login): the title,
 * "Enter your 4-digit PIN", four dots, and a 3×4 keypad whose bottom row is
 * Clear / 0 / Delete.
 *
 * Just the PIN — "4-digit PIN login, no usernames", as the brief says and as
 * the owner asked on 23 September 2026. The tablet tries the PIN against
 * everyone who can log in and the one it opens is who you are, so PINs are
 * unique across people, and five wrong PINs lock the pad on this tablet for a
 * quarter of an hour.
 *
 * One deliberate omission: the prototype's `renderPin()` prints every user's PIN
 * under the pad as a demo hint. That line is not ported — see CLAUDE.md,
 * "Corrections to the prototype", row 16.
 *
 * The PIN is held in a local variable for as long as it takes to try it, and
 * never leaves this device: it decrypts a cached credential, and the
 * credential is what the server sees.
 */

interface Props {
  people: PersonSummary[];
  tabletLabel: string;
  /** Fetch who can log in again — a new login, a new PIN. */
  onRefresh: () => void;
}

const KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9'] as const;

export default function Login({ people, tabletLabel, onRefresh }: Props) {
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [attemptsLeft, setAttemptsLeft] = useState(MAX_PIN_ATTEMPTS);
  const [lockedUntil, setLockedUntil] = useState<string | null>(null);
  const [, tick] = useState(0);

  const withPins = people.filter((p) => p.hasPin).length;

  // The pad's own state survives a reload; read it, and count a lock down.
  useEffect(() => {
    void padState().then((state) => {
      setAttemptsLeft(state.attemptsLeft);
      setLockedUntil(state.lockedUntil);
    });
  }, []);
  useEffect(() => {
    if (!lockedUntil) return;
    const timer = setInterval(() => {
      if (Date.parse(lockedUntil) <= Date.now()) {
        setLockedUntil(null);
        setAttemptsLeft(MAX_PIN_ATTEMPTS);
        setError('');
      } else {
        tick((n) => n + 1);
      }
    }, 1000);
    return () => clearInterval(timer);
  }, [lockedUntil]);

  const submit = useCallback(async (candidate: string) => {
    setBusy(true);
    setError('');
    try {
      await unlockWithPinOnly(candidate);
      // The shell takes over from here; this component unmounts.
    } catch (err) {
      setPin('');
      if (err instanceof PadLockedError) {
        setLockedUntil(err.until);
        setAttemptsLeft(0);
        setError(err.message);
      } else if (err instanceof PinError) {
        setAttemptsLeft(err.attemptsLeft);
        setError(err.message);
      } else if (err instanceof ApiError) {
        setError(err.message);
      } else if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Could not sign in. Try again.');
      }
    } finally {
      setBusy(false);
    }
  }, []);

  const locked = lockedUntil !== null && Date.parse(lockedUntil) > Date.now();

  const press = useCallback(
    (key: string) => {
      if (busy || locked) return;
      setError('');
      setPin((previous) => {
        if (key === 'c') return '';
        if (key === 'b') return previous.slice(0, -1);
        if (previous.length >= 4) return previous;
        const next = previous + key;
        // The prototype checks on the fourth digit with a short delay so the
        // last dot is seen to fill.
        if (next.length === 4) setTimeout(() => void submit(next), 120);
        return next;
      });
    },
    [busy, locked, submit],
  );

  // A physical keyboard works too, as it does in the prototype.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (/^[0-9]$/.test(event.key)) press(event.key);
      else if (event.key === 'Backspace') press('b');
      else if (event.key === 'Escape') press('c');
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [press]);

  const minutesLeft = lockedUntil ? Math.max(1, Math.ceil((Date.parse(lockedUntil) - Date.now()) / 60_000)) : 0;
  const warnAttempts = !locked && attemptsLeft < MAX_PIN_ATTEMPTS && attemptsLeft > 0;

  return (
    <div className="login">
      <div className="loginbox">
        <div style={{ fontSize: 21, fontWeight: 500 }}>Quarry cost tracker</div>
        <div className="sub" style={{ marginTop: 4 }}>
          Enter your 4-digit PIN
        </div>

        <div className="dots" aria-hidden="true">
          {[0, 1, 2, 3].map((i) => (
            <span key={i} className={i < pin.length ? 'f' : ''} />
          ))}
        </div>

        <div className="pad">
          {KEYS.map((key) => (
            <button key={key} type="button" onClick={() => press(key)} disabled={busy || locked}>
              {key}
            </button>
          ))}
          <button type="button" className="alt" onClick={() => press('c')} disabled={busy || locked}>
            Clear
          </button>
          <button type="button" onClick={() => press('0')} disabled={busy || locked}>
            0
          </button>
          <button type="button" className="alt" onClick={() => press('b')} disabled={busy || locked}>
            Delete
          </button>
        </div>

        <div className="err" role="alert">
          {busy ? '' : error}
        </div>

        <div className="sub" style={{ marginTop: 6 }}>
          {busy ? 'Checking…' : tabletLabel}
        </div>

        {locked && (
          <div className="banner b-no" style={{ marginTop: 14, justifyContent: 'center' }}>
            Too many wrong PINs. Try again in {minutesLeft} minute{minutesLeft === 1 ? '' : 's'}.
          </div>
        )}

        {warnAttempts && (
          <div className="banner b-warn" style={{ marginTop: 14, justifyContent: 'center' }}>
            {attemptsLeft} {attemptsLeft === 1 ? 'try' : 'tries'} left before this tablet locks
            itself for a while.
          </div>
        )}

        {withPins === 0 && (
          <div className="banner b-warn" style={{ marginTop: 14, fontSize: 13 }}>
            Nobody can log in here yet. Connect this tablet to the office once so it can fetch
            who can log in, and make sure the owner has set PINs in Settings.
          </div>
        )}

        <button type="button" className="btn sm" style={{ marginTop: 18, minWidth: 0 }} onClick={onRefresh} disabled={busy}>
          {withPins === 0 ? 'Fetch who can log in' : 'Somebody new? Fetch the list again'}
        </button>
      </div>
    </div>
  );
}
